import getSymbolFromCurrency from 'currency-symbol-map'
import React, { SetStateAction, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'

import { AddAccountPaths, Paths } from '../../config/routes'
import { formatCryptoAmount } from '../../lib/helpers'
import { MasaAccount } from '../../models'
import { AccountItem } from '../account-item/account-item'
import { BankAccountItemSkeleton } from '../bank-account-item'
import { Card } from '../card'
import { CurrencySelector } from './currency-selector'

export const I18N = {
    HEADING: 'Linked accounts',
    NOT_FOUND: 'No accounts found',
    LINK_TO_SCREEN: 'View & Connect',
    LINK_TO_CONNECT: 'Connect more',
}

export interface AccountsListProps {
    /** AccountsList accounts */
    accounts?: MasaAccount[]
    /** Accounts grouped by currency */
    groupedByAccounts?: any[]
    /** Disables the button */
    disabled?: boolean
    /** Renders a spinner */
    loading?: boolean
    /** Renders the AccountsList full width */
    className?: string
    /** This indicates if the component will show every account + filters */
    full?: boolean
    /** This indicates that h-full will be active */
    fillVertical?: boolean
    /** Group */
    group?: boolean
    /** */
    groupBy?: any[]
    /** Currency filter */
    currency?: string | null
    /** Selected or active account */
    selectedAccount?: string | null
    /** UpdateCurrency */
    updateCurrency?: (currency: string) => void
    /** Optional click handler */
    onClick?:
        | ((account: MasaAccount) => void)
        | ((currency: string, type: SetStateAction<'bank' | 'crypto'>) => void)
}

/**
 * Default AccountList component
 */
export function AccountsList({
    className,
    accounts = [],
    groupedByAccounts = [],
    loading = false,
    full = false,
    onClick = () => {},
    group = true,
    selectedAccount = undefined,
    updateCurrency = () => {},
    fillVertical = false,
    ...props
}: AccountsListProps) {
    const history = useHistory()
    const [skeletonsAmount, setSkeletonsAmount] = useState(10)
    const accountListClasses = [
        'flex flex-col justify-between gap-y-4 w-full',
        full ? 'h-full' : '',
        fillVertical ? 'h-full' : '',
    ].join(' ')

    const firstTwoAccounts = useMemo(() => accounts.slice(0, 2), [accounts])
    const accountsList = useMemo(
        () => (full ? accounts : firstTwoAccounts),
        [accounts, full, firstTwoAccounts],
    )

    const handleRenderAccount = (account: MasaAccount, idx: number) => {
        const isActive = selectedAccount
            ? selectedAccount === account.id ||
              selectedAccount === account.accountId
            : false
        return (
            <AccountItem
                className='rounded-md'
                name={`${account?.provider} - ${
                    account.type === 'CRYPTO'
                        ? getSymbolFromCurrency(
                              account?.amount?.currency ?? ' ',
                          ) ?? account?.amount?.currency
                        : account?.amount?.currency
                } ${
                    account.type === 'CRYPTO' ? account?.amount?.value ?? 0 : ''
                }`}
                iconName={account?.amount?.currency ?? ''}
                lastUpdatedOne={new Date((account as any).lastUpdated)}
                balanceInfo={{
                    current:
                        account?.amount?.fiatValue ??
                        account?.amount?.value ??
                        0,
                    isoCurrencyCode:
                        getSymbolFromCurrency(
                            account?.amount?.fiatCurrency ?? 'usd',
                        ) ?? ' ',
                }}
                key={`account-${idx}`}
                onClick={() =>
                    onClick(
                        selectedAccount === account.id
                            ? null
                            : (account as any),
                        account?.type?.toLowerCase() as SetStateAction<
                            'bank' | 'crypto'
                        >,
                    )
                }
                active={isActive}
                isCrypto
            />
        )
    }

    const handleRenderGroups = () => {
        const groups = []
        for (let currency in groupedByAccounts) {
            const accs = groupedByAccounts[currency]
            const balance = accs.reduce((acc: number, account: MasaAccount) => {
                if (typeof acc === 'number')
                    // @ts-ignore: Object is possibly 'undefined'.
                    return acc + parseFloat(account.amount.value)
                // @ts-ignore: Object is possibly 'undefined'.
                return account.amount.value
            }, 0)
            const { length } = accs

            const isCrypto = accs.some(
                (acc: MasaAccount) => acc.type !== 'BANK',
            )
            const lastTimeUpdated = accs[0].lastUpdated
            groups.push({
                accs,
                balance,
                length,
                currency,
                isCrypto,
                lastTimeUpdated,
            })
        }

        return groups.map((group, idx) => {
            return (
                <AccountItem
                    className='rounded-md'
                    name={`${group.currency} accounts`}
                    iconName={group.currency}
                    balanceInfo={{
                        current:
                            parseFloat(formatCryptoAmount(group?.balance)) ?? 0,
                        isoCurrencyCode:
                            getSymbolFromCurrency(group.currency) ??
                            group.currency,
                    }}
                    lastUpdatedOne={new Date((group as any).lastTimeUpdated)}
                    key={`account-group-${idx}`}
                    onClick={() => {
                        setSkeletonsAmount(group.accs.length)
                        onClick(
                            group.currency,
                            group.isCrypto ? 'crypto' : 'bank',
                        )
                    }}
                    length={group.accs.length}
                    isCrypto={group.isCrypto}
                    active={!props.currency ? false : true}
                />
            )
        })
    }

    const Loading = useMemo(
        () =>
            Array(skeletonsAmount)
                .fill(0)
                .map((i, idx) => (
                    <BankAccountItemSkeleton
                        key={`skeleton-account-${i + idx}`}
                    />
                )),
        [skeletonsAmount],
    )

    const dropdownOptions = useMemo(() => {
        if (!accounts) return []
        const options: { label: string; value: string | undefined }[] = [
            { label: 'All Accounts', value: undefined },
        ]
        accounts.forEach((op: any) => {
            if (!options.some((o: any) => o.value === op.amount.currency)) {
                options.push({
                    label: `${op.amount.currency} Accounts`,
                    value: op.amount.currency,
                })
            }
        })
        return options
    }, [accounts])

    return (
        <Card
            testId='linked-accounts'
            className={`${accountListClasses} ${className}`}
            role='table'
            aria-label={I18N.HEADING}
            {...props}>
            <div
                className='flex flex-row items-center justify-between w-full'
                role='row'>
                <h3 role='columnheader' className='m-0'>
                    {props.currency
                        ? `${props.currency} Accounts`
                        : I18N.HEADING}
                </h3>
                {full && (
                    <CurrencySelector
                        //@ts-ignore
                        onChange={updateCurrency}
                        defaultValue={dropdownOptions.find(
                            o => o.value === props.currency,
                        )}
                    />
                )}
            </div>
            {!loading && group && groupedByAccounts ? (
                <div className='flex flex-col justify-start flex-grow overflow-scroll gap-y-2 scrollbar-hide'>
                    {handleRenderGroups()}
                </div>
            ) : null}

            {!loading &&
                (group
                    ? groupedByAccounts.length === 0
                    : accountsList.length === 0) && (
                    <p className='w-full mt-4 text-center'>
                        There are no accounts, try adding them{' '}
                        <u
                            className='cursor-pointer'
                            onClick={() =>
                                history.push(AddAccountPaths.ADD_BANK_ACCOUNT)
                            }>
                            here
                        </u>
                    </p>
                )}

            {!loading && !group && accountsList.length ? (
                <div className='flex flex-col justify-start flex-grow overflow-scroll gap-y-2 scrollbar-hide'>
                    {accountsList.map(handleRenderAccount)}
                </div>
            ) : null}

            {loading && (
                <div className='flex flex-col justify-start flex-grow overflow-scroll gap-y-2 scrollbar-hide'>
                    {Loading}
                </div>
            )}

            {full ? (
                <Link
                    to={AddAccountPaths.ADD_ACCOUNT}
                    className='w-full mt-2 text-center cursor-pointer'>
                    <h3 className='m-0'>{I18N.LINK_TO_CONNECT}</h3>
                </Link>
            ) : (
                <Link
                    to={Paths.ACCOUNTS}
                    className='w-full mt-2 text-center cursor-pointer'>
                    <h3 className='m-0'>{I18N.LINK_TO_SCREEN}</h3>
                </Link>
            )}
        </Card>
    )
}
